/**
 * JOINING BY INVITATION (`/join#<token>`, migration 0034).
 *
 * Opened from the link a colleague sent. The link is the credential: it lives
 * in the URL FRAGMENT, which a browser never sends to a server, and leaves
 * this page only inside a request BODY. Every way a link can fail reads the
 * same, because the server answers them the same.
 *
 * The page says who invited the person, to which firm or team, and as what,
 * then asks only for their name and a password; the email is the one the
 * invitation was sent to. Joining signs them in and opens Leveri.
 *
 * Mounted BEFORE the app's sign-in gate (main.tsx), with no session call.
 */
import { useEffect, useState, type FormEvent } from "react";
import type { z } from "zod";
import { ErrorEnvelope, PASSWORD_MIN_LENGTH, PASSWORD_RULES, PublicInvitationResponse, SessionResponse } from "@leveri/contracts";
import { Button, Notice, Reading, TextField } from "../file/index.js";
import { Wordmark } from "./shell.js";

type Invitation = z.infer<typeof PublicInvitationResponse>["invitation"];
const tokenFromFragment = (): string => window.location.hash.replace(/^#/, "");
const ROLE_WORDS: Record<Invitation["role"], string> = { PARTNER: "a partner", MANAGER: "a manager", STAFF: "a member of staff" };

async function post<T extends z.ZodTypeAny>(path: string, schema: T, body: unknown): Promise<z.infer<T>> {
  const res = await fetch(path, {
    method: "POST", credentials: "same-origin", body: JSON.stringify(body),
    headers: { accept: "application/json", "content-type": "application/json" },
  });
  const payload: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const envelope = ErrorEnvelope.safeParse(payload);
    throw new Error(envelope.success ? envelope.data.error.message : "Something went wrong. Try again in a moment.");
  }
  return schema.parse(payload);
}

export function JoinPage() {
  const [token] = useState(tokenFromFragment);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    post("/api/public/invitation", PublicInvitationResponse, { token })
      .then((r) => setInvitation(r.invitation))
      .catch((err: unknown) => setFailure(err instanceof Error ? err.message : String(err)));
  }, [token]);

  async function join(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setFailure(null);
    try {
      await post("/api/public/invitation/accept", SessionResponse, {
        token, displayName: String(form.get("displayName") ?? ""), password: String(form.get("password") ?? ""),
      });
      // Signed in: the token leaves the address bar with the page.
      window.location.replace("/");
    } catch (err) {
      setFailure(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-6 py-14 text-ink">
      <div className="w-full max-w-[380px]">
        <div className="mb-10"><Wordmark /></div>
        {!invitation && !failure && <Reading>Opening the invitation…</Reading>}
        {!invitation && failure && (
          <>
            <h1 className="text-[22px] font-semibold tracking-[-0.01em]">This invitation cannot be used</h1>
            <p className="mt-2 text-sm text-stone" data-testid="join-invalid">{failure}</p>
          </>
        )}
        {invitation && (
          <>
            <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Join {invitation.firmName}</h1>
            <p className="mt-1.5 text-sm text-stone">
              {invitation.invitedByName} invited you as {ROLE_WORDS[invitation.role]}. The invitation is open until {invitation.expiresAtText}.
            </p>
            <form onSubmit={join} className="mt-8 flex flex-col gap-5">
              <div>
                <div className="mb-1.5 text-sm font-medium text-ink-soft">Work email</div>
                <div className="text-[15px] text-ink" data-testid="join-email">{invitation.email}</div>
              </div>
              <TextField name="displayName" label="Your name" required autoComplete="name" className={TALL} />
              <TextField name="password" type="password" label="Password" hint={PASSWORD_RULES.join(" ")} required minLength={PASSWORD_MIN_LENGTH} autoComplete="new-password" className={TALL} />
              {failure && <Notice>{failure}</Notice>}
              <Button type="submit" disabled={busy} className="mt-1 !h-11 w-full !text-[15px]" data-testid="join-submit">Join {invitation.firmName}</Button>
              {busy && <Reading>Joining…</Reading>}
            </form>
          </>
        )}
      </div>
    </main>
  );
}

const TALL = "!h-11 !text-[15px]";
